import { Link } from 'react-router';
import { LIBELLE_STATUT_REMUNERATION, LIBELLE_STATUT_VERSION_BAREME, type BlocPaie, type StatutRemuneration, type StatutVersionBareme } from '@/api/types';
import { Kpi } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, dateFr } from '@/lib/utils';

const VARIANTE_STATUT: Record<StatutRemuneration, 'default' | 'secondary' | 'destructive' | 'outline'> = { IMPORTEE: 'secondary', A_REGULARISER: 'destructive', CONTROLEE: 'outline', APPROUVEE: 'default' };
export const StatutRemunerationBadge = ({ statut }: { statut: StatutRemuneration }) => (
  <Badge variant={VARIANTE_STATUT[statut]} className={cn(statut === 'APPROUVEE' && 'border-emerald-300 bg-emerald-50 text-emerald-800', statut === 'CONTROLEE' && 'border-sky-300 bg-sky-50 text-sky-800')}>
    {LIBELLE_STATUT_REMUNERATION[statut]}
  </Badge>
);

const VARIANTE_VERSION: Record<StatutVersionBareme, 'default' | 'secondary' | 'outline'> = { PREPARATION: 'secondary', EN_VIGUEUR: 'default', ARCHIVEE: 'outline' };
export const StatutVersionBadge = ({ statut }: { statut: StatutVersionBareme }) => (
  <Badge variant={VARIANTE_VERSION[statut]} className={cn(statut === 'EN_VIGUEUR' && 'border-emerald-300 bg-emerald-50 text-emerald-800')}>
    {LIBELLE_STATUT_VERSION_BAREME[statut]}
  </Badge>
);

/** Bloc du tableau de bord (lot HP-2) : sa fiche pour tout agent ; barème, fiches et anniversaires pour les profils de la paie. */
export function BlocPaieTableau({ bloc }: { bloc: BlocPaie }) {
  const p = bloc.paie;
  return (
    <Card data-testid="bloc-paie">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2">
          Rémunérations de référence
          <span className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/rh/mon-dossier?onglet=remuneration">Ma fiche</Link>
            </Button>
            {p && (
              <Button asChild size="sm" variant="outline">
                <Link to="/paie/remunerations">Fiches de rémunération</Link>
              </Button>
            )}
          </span>
        </CardTitle>
        <CardDescription>Barème versionné, annuités d'ancienneté, fiches contrôlées par les RH, approuvées par le Directeur RH et visées par le Directeur financier ; hors présences et retenues.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/rh/mon-dossier?onglet=remuneration">
          <Kpi label="Ma fiche de référence" valeur={bloc.mien.statut ? LIBELLE_STATUT_REMUNERATION[bloc.mien.statut] : 'à constituer'} detail={bloc.mien.effetDu ? `en vigueur depuis le ${dateFr(bloc.mien.effetDu)}` : 'consultable dans votre dossier HRMS'} accent={bloc.mien.statut === 'APPROUVEE' ? 'ok' : undefined} />
        </Link>
        {p && (
          <>
            <Link to="/paie/bareme">
              <Kpi label="Barème en vigueur" valeur={p.bareme ? `version ${p.bareme.numero}` : 'aucun'} detail={p.bareme ? `${p.bareme.libelle} · effet du ${dateFr(p.bareme.debut)}${p.impot ? ' · impôt paramétré' : ' · barème d’impôt à renseigner'}` : 'à préparer et approuver (DF, DG)'} accent={p.bareme ? (p.impot ? 'ok' : 'alerte') : 'critique'} />
            </Link>
            <Link to="/paie/remunerations?statut=A_REGULARISER">
              <Kpi label="Fiches à régulariser" valeur={p.aRegulariser} detail={`${p.fiches} fiche(s) · ${p.controlees} contrôlée(s) · ${p.approuvees} approuvée(s)${p.aApprouver !== null ? ` · ${p.aApprouver} à approuver (DRH)` : ''}${p.aViser !== null ? ` · ${p.aViser} à viser (DF)` : ''}`} accent={p.aRegulariser > 0 ? 'alerte' : 'ok'} />
            </Link>
            <Link to="/paie/remunerations?anniversaires=true">
              <Kpi label="Anniversaires dans 30 jours" valeur={p.anniversaires30Jours} detail="annuités à venir (classes A, B ; cadres de direction)" />
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
}
